import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  FileText,
  ListChecks,
  Clock,
  Users,
} from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * WiedzaJakUporzadkowacAktaImigracyjne
 *
 * Second SEO long-form article (PL) — practical step-by-step on how to
 * organize immigration records before speaking with a lawyer, NGO, or
 * institution. Anchors the "/moje-akta-imigracyjne" privacy-safe draft
 * flow that now exists for real.
 *
 * Strict scope:
 *  - Static page. No state. No forms. No uploads. No API. No auth.
 *  - Backend untouched.
 *  - Tone: practical · calm · people-first · institution-friendly.
 *  - No legal advice. No status determination. No certification claim.
 *  - No LW references.
 */
const META_TITLE = 'Jak uporządkować akta imigracyjne | HussarAi';
const META_DESCRIPTION =
  'Praktyczny przewodnik: jak uporządkować dokumenty imigracyjne, ' +
  'terminy, historię pobytu i pytania do weryfikacji przez człowieka.';
const CANONICAL_URL =
  'https://hussarai.org/wiedza/jak-uporzadkowac-akta-imigracyjne';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    if (selector.includes('property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) el.setAttribute('property', prop);
    } else if (selector.includes('name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) el.setAttribute('name', name);
    } else if (selector.includes('rel=')) {
      const rel = selector.match(/rel="([^"]+)"/)?.[1];
      if (rel) el.setAttribute('rel', rel);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const WiedzaJakUporzadkowacAktaImigracyjne = () => {
  useEffect(() => {
    document.title = META_TITLE;
    setMeta('meta[name="description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:title"]', 'content', META_TITLE);
    setMeta('meta[property="og:description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:type"]', 'content', 'article');
    setMeta('meta[property="og:url"]', 'content', CANONICAL_URL);
    setMeta('link[rel="canonical"]', 'href', CANONICAL_URL);
  }, []);

  return (
    <main
      data-testid="wiedza-jak-uporzadkowac-page"
      style={{
        minHeight: '100vh',
        background: 'var(--hussar-bg-page, #0B1220)',
        color: 'var(--hussar-text-primary, #E5E7EB)',
        padding: '56px 20px 96px',
      }}
    >
      <HussarTopNav />
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="wiedza-jak-back-link"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'rgba(125,211,252,0.85)',
            textDecoration: 'none', marginBottom: 28,
          }}
        >
          <ArrowLeft className="w-4 h-4" /> Powrót do HussarAi
        </Link>

        <p
          data-testid="wiedza-jak-eyebrow"
          style={{
            fontSize: 11, letterSpacing: 1.6, fontWeight: 700,
            color: '#7DD3FC', textTransform: 'uppercase', marginBottom: 14,
          }}
        >
          WIEDZA · AKTA IMIGRACYJNE · PRZED ROZMOWĄ Z CZŁOWIEKIEM
        </p>

        <h1
          data-testid="wiedza-jak-h1"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800,
            lineHeight: 1.2, marginBottom: 18, color: '#F1F5F9',
          }}
        >
          Jak uporządkować akta imigracyjne przed rozmową
          z&nbsp;prawnikiem, NGO lub instytucją
        </h1>

        <p
          data-testid="wiedza-jak-lede"
          style={{
            fontSize: 18, lineHeight: 1.55, fontWeight: 600,
            color: '#F1F5F9', marginBottom: 8,
          }}
        >
          Twoja historia nie powinna zniknąć w&nbsp;papierach.
        </p>

        <p
          data-testid="wiedza-jak-intro"
          style={{
            fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)',
            marginBottom: 28,
          }}
        >
          Dokumenty imigracyjne rzadko leżą w&nbsp;jednym miejscu. Część jest
          w&nbsp;mailu, część w&nbsp;PDF na pulpicie, część w&nbsp;pismach
          urzędowych w&nbsp;szufladzie, część u&nbsp;pracodawcy, część
          w&nbsp;papierach dziecka ze&nbsp;szkoły. Do&nbsp;tego dochodzą
          terminy: kiedy kończy się karta pobytu, kiedy trzeba złożyć wniosek
          o&nbsp;zezwolenie na pracę, kiedy mija termin na&nbsp;odpowiedź
          urzędową. Ten artykuł pokazuje, jak uporządkować akta imigracyjne
          krok po&nbsp;kroku, zanim usiądziesz z&nbsp;prawnikiem, NGO,
          pracodawcą lub urzędnikiem — tak żeby rozmowa była o&nbsp;sprawie,
          a&nbsp;nie o&nbsp;szukaniu papierów.
        </p>

        <h2
          data-testid="wiedza-jak-h2-what-are-records"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Co znaczą „akta imigracyjne”?
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          „Akta imigracyjne” to nie jeden dokument. To suma materiałów, które
          razem pokazują obecność osoby w&nbsp;Polsce lub innych krajach.
          W&nbsp;praktyce składają się na nie:
        </p>
        <ul
          data-testid="wiedza-jak-records-list"
          style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}
        >
          <li>kontekst tożsamości (paszport, dowód, akty stanu cywilnego);</li>
          <li>historia krajów i&nbsp;podróży (wjazdy, wyjazdy, daty pobytu w&nbsp;poszczególnych krajach);</li>
          <li>dokumenty pobytowe (wiza, karta pobytu, decyzje o&nbsp;zezwoleniu na pobyt czasowy lub stały);</li>
          <li>zezwolenie na pracę i&nbsp;umowy (oświadczenia pracodawcy, kontrakty, dokumenty rejestracji firmy);</li>
          <li>dokumenty szkolne i&nbsp;uniwersyteckie (zaświadczenia, dyplomy, listy z&nbsp;uczelni, dokumenty dziecka);</li>
          <li>dokumenty rodzinne (małżonek, dzieci, osoby zależne, sponsorzy);</li>
          <li>wnioski i&nbsp;pisma urzędowe (wezwania, postanowienia, odwołania);</li>
          <li>terminy imigracyjne (kiedy kończy się dokument, kiedy mija termin odpowiedzi);</li>
          <li>notatki z&nbsp;wcześniejszej weryfikacji (prawnik, NGO, doradca).</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          Im bardziej rozproszone są te dokumenty, tym dłużej trwa rozmowa
          z&nbsp;pomocnikiem i&nbsp;tym łatwiej coś przeoczyć. Dlatego
          pierwszym krokiem nie jest składanie wniosków — jest porządkowanie
          historii.
        </p>

        <h2
          data-testid="wiedza-jak-h2-checklist"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Krok po&nbsp;kroku: jak zacząć
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Poniżej praktyczna lista. Nie musisz mieć wszystkiego od&nbsp;razu —
          chodzi o&nbsp;to, żeby zacząć od&nbsp;tego, co masz teraz, i&nbsp;na
          bieżąco dopisywać.
        </p>
        <ol
          data-testid="wiedza-jak-checklist-list"
          style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.75, marginBottom: 18 }}
        >
          <li>
            <strong>Zbierz kontekst tożsamości i&nbsp;rodziny.</strong> Wypisz,
            kto jest w&nbsp;sprawie: ty, małżonek, dzieci, osoby zależne,
            sponsorzy. Nie podawaj jeszcze numerów dokumentów ani adresów —
            wystarczą role i&nbsp;relacje (np. „małżonek, obywatel UE”,
            „dziecko, uczy się w&nbsp;Polsce”).
          </li>
          <li>
            <strong>Wypisz kraje i&nbsp;daty pobytu.</strong> Stwórz prostą
            oś czasu: kraj — od&nbsp;kiedy — do&nbsp;kiedy. Jeśli czegoś nie
            pamiętasz, zaznacz to&nbsp;jako lukę („data niepewna”). Lepiej
            uczciwa luka niż wymyślona data.
          </li>
          <li>
            <strong>Uporządkuj dokumenty pobytowe.</strong> Wypisz, jakie
            dokumenty pobytowe miałeś / masz: wiza, karta pobytu, decyzja
            o&nbsp;zezwoleniu na pobyt czasowy / stały, dokumenty do&nbsp;karty
            pobytu. Zanotuj typ dokumentu i&nbsp;rok wydania.
          </li>
          <li>
            <strong>Uporządkuj dokumenty pracy i&nbsp;szkoły.</strong> Wypisz
            pracodawców i&nbsp;okresy zatrudnienia. Dla każdego pracodawcy:
            czy było zezwolenie na pracę, oświadczenie pracodawcy, umowa.
            Dla szkoły / uczelni: zaświadczenia, dyplomy, dokumenty dziecka.
          </li>
          <li>
            <strong>Zbierz wnioski o&nbsp;pobyt i&nbsp;pisma urzędowe.</strong>
            Wypisz, jakie wnioski składałeś (np. wniosek o&nbsp;pobyt
            dokumenty, wniosek o&nbsp;zezwolenie na pracę, odwołanie),
            kiedy, oraz jakie odpowiedzi urzędowe otrzymałeś.
          </li>
          <li>
            <strong>Zapisz terminy imigracyjne.</strong> Kiedy kończy się
            karta pobytu, kiedy mija termin na&nbsp;odpowiedź, kiedy trzeba
            złożyć wniosek o&nbsp;przedłużenie. Termin „za&nbsp;30 dni” jest
            ważniejszy niż termin „za&nbsp;rok”.
          </li>
          <li>
            <strong>Oznacz brakujące dokumenty.</strong> To jest najważniejsza
            wartość przygotowania: nie udawać, że masz wszystko. Lista
            brakujących pozycji oszczędza godziny pomocnikowi.
          </li>
          <li>
            <strong>Oznacz niespójne daty lub sprzeczne informacje.</strong>
            Jeśli na&nbsp;jednym dokumencie widnieje data wjazdu różna od&nbsp;tej
            na drugim, zapisz to&nbsp;jako pytanie do&nbsp;weryfikacji.
          </li>
          <li>
            <strong>Przygotuj pytania do&nbsp;weryfikacji przez człowieka.</strong>
            Krótka lista konkretnych pytań („Czy mogę złożyć wniosek o&nbsp;pobyt
            stały już teraz?”, „Jakich dokumentów brakuje?”) jest skuteczniejsza
            niż jedno duże pytanie „co dalej?”.
          </li>
        </ol>

        <h2
          data-testid="wiedza-jak-h2-dont-enter"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Czego jeszcze nie wpisywać
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Publiczny formularz <Link
            to="/moje-akta-imigracyjne"
            data-testid="wiedza-jak-link-moje-akta-inline"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Moje Akta Imigracyjne
          </Link> celowo jest minimalny i&nbsp;bezpieczny. Nie obsługuje
          jeszcze przesyłania plików ani pełnej treści dokumentów. Dlatego
          na&nbsp;tym etapie nie wpisuj jeszcze:
        </p>
        <ul
          data-testid="wiedza-jak-dont-enter-list"
          style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}
        >
          <li>numeru paszportu;</li>
          <li>numeru PESEL;</li>
          <li>numeru dowodu / krajowego numeru identyfikacyjnego;</li>
          <li>dokładnej daty urodzenia;</li>
          <li>pełnego adresu zamieszkania;</li>
          <li>numeru telefonu;</li>
          <li>skanów dokumentów;</li>
          <li>pełnej treści dokumentów (np. całej decyzji urzędowej);</li>
          <li>danych osobowych dzieci.</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          To nie jest brak — to świadoma granica. Wrażliwe dane mają trafić
          do&nbsp;uprawnionego człowieka (prawnik, NGO, urząd) bezpiecznym
          kanałem, nie do&nbsp;szybkiego formularza w&nbsp;internecie.
        </p>

        <h2
          data-testid="wiedza-jak-h2-how-hussar-helps"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Jak HussarAi pomaga
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          HussarAi pomaga utworzyć szkic akt imigracyjnych, uporządkować
          kontekst (tożsamość, rodzina, kraje, dokumenty, praca, szkoła,
          terminy, pytania) i&nbsp;przygotować materiał do&nbsp;weryfikacji
          przez człowieka. W&nbsp;tle każda zmiana trafia do&nbsp;śladu
          audytowego — kto, co, kiedy zmienił.
        </p>
        <ul
          data-testid="wiedza-jak-how-list"
          style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}
        >
          <li>
            <Link
              to="/moje-akta-imigracyjne"
              data-testid="wiedza-jak-link-moje-akta-bullet"
              style={{ color: '#7DD3FC', fontWeight: 600 }}
            >
              /moje-akta-imigracyjne
            </Link>{' '}
            — pozwala teraz utworzyć bezpieczny szkic akt (privacy-safe draft).
          </li>
          <li>nie ma jeszcze przesyłania plików — szkic jest tekstowy i&nbsp;minimalny;</li>
          <li>HussarAi nie udziela porad prawnych;</li>
          <li>HussarAi nie ustala statusu imigracyjnego;</li>
          <li>HussarAi nie potwierdza automatycznie żadnego wniosku;</li>
          <li>każdy szkic wymaga weryfikacji przez człowieka.</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          AI może odpowiedzialnie pomóc w&nbsp;spójności osi czasu
          (terminy imigracyjne, daty wjazdów, terminy odpowiedzi), w&nbsp;wykryciu
          brakujących informacji oraz w&nbsp;przygotowaniu pakietu pytań
          do&nbsp;człowieka weryfikującego. Nie podejmuje decyzji o&nbsp;statusie.
        </p>

        <h2
          data-testid="wiedza-jak-h2-boundary"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Granica AI
        </h2>
        <p
          data-testid="wiedza-jak-doctrine"
          style={{
            fontSize: 15, lineHeight: 1.6, marginBottom: 18,
            color: 'rgba(125,211,252,0.92)', fontStyle: 'italic',
            borderLeft: '3px solid #7DD3FC', paddingLeft: 14,
          }}
        >
          AI przygotowuje. Władza ludzka decyduje. Każde działanie zostawia
          ślad audytowy.
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          Modele językowe nie powinny rozstrzygać, czy człowiek ma prawo
          do&nbsp;pobytu, do&nbsp;pracy, do&nbsp;edukacji dziecka albo
          do&nbsp;łączenia rodziny. To są decyzje uprawnionych ludzi
          i&nbsp;organizacji. AI w&nbsp;aktach imigracyjnych ma sens wtedy,
          kiedy porządkuje materiał — historię pobytu, terminy, pytania,
          braki — i&nbsp;oddaje go człowiekowi do&nbsp;weryfikacji.
        </p>
        <div
          data-testid="wiedza-jak-safety-card"
          style={{
            padding: '18px 20px', marginTop: 6, marginBottom: 24,
            background: 'rgba(248,113,113,0.06)',
            border: '1px solid rgba(248,113,113,0.30)',
            borderLeft: '3px solid #F87171',
            borderRadius: 12,
          }}
        >
          <p style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>
            Granica systemu
          </p>
          <p
            data-testid="wiedza-jak-safety-body"
            style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8, color: 'rgba(226,232,240,0.92)' }}
          >
            HussarAi nie udziela porad prawnych, nie ustala statusu
            imigracyjnego i&nbsp;przygotowuje akta wyłącznie do&nbsp;weryfikacji
            przez człowieka.
          </p>
        </div>

        <h2
          data-testid="wiedza-jak-h2-helpers"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Dla prawników, NGO, pracodawców i&nbsp;instytucji
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Ustrukturyzowane akta pozwalają pomocnikom pracować szybciej
          i&nbsp;bezpieczniej. Dla człowieka weryfikującego oznacza to:
        </p>
        <ul
          data-testid="wiedza-jak-helpers-list"
          style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}
        >
          <li>czytelna oś czasu zamiast luźnych dokumentów;</li>
          <li>krótsza lista brakujących dokumentów (np. dokumenty do&nbsp;karty pobytu);</li>
          <li>lepsze pytania od&nbsp;osoby zainteresowanej — od&nbsp;razu konkretne;</li>
          <li>bezpieczniejsza weryfikacja przez człowieka — bez wrażliwych danych w&nbsp;szkicu;</li>
          <li>bardziej odpowiedzialny ślad audytowy (kto i&nbsp;kiedy co zmienił).</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          Więcej o&nbsp;warstwie zgodności i&nbsp;nadzoru: <Link
            to="/operator-zgodnosci"
            data-testid="wiedza-jak-link-compliance"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Operator Zgodności dla akt imigracyjnych
          </Link> oraz <Link
            to="/bezpieczenstwo-i-zgodnosc"
            data-testid="wiedza-jak-link-security"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Bezpieczeństwo i&nbsp;nadzór nad aktami imigracyjnymi
          </Link>. Dla pełnego widoku procesu zobacz <Link
            to="/dashboard-akt-imigracyjnych"
            data-testid="wiedza-jak-link-dashboard"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Hub Akt Imigracyjnych
          </Link>. Dalsza lektura: <Link
            to="/wiedza/ai-w-aktach-imigracyjnych"
            data-testid="wiedza-jak-link-prev-article"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            AI w&nbsp;aktach imigracyjnych: jak uporządkować dokumenty bez
            oddawania decyzji maszynie
          </Link>.
        </p>

        <h2
          data-testid="wiedza-jak-h2-conclusion"
          style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}
        >
          Podsumowanie
        </h2>
        <p
          data-testid="wiedza-jak-conclusion"
          style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}
        >
          Uporządkowane akta imigracyjne nie są luksusem — są punktem
          wyjścia każdej rozmowy z&nbsp;prawnikiem, NGO, pracodawcą lub
          urzędem. Zaczynasz od&nbsp;siebie: kontekstu, krajów, dokumentów,
          terminów, braków i&nbsp;pytań. AI porządkuje. Człowiek decyduje.
          Każde działanie zostawia ślad audytowy. Bo&nbsp;Twoja historia nie powinna zniknąć w&nbsp;papierach.
        </p>

        <div
          data-testid="wiedza-jak-cta-row"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 12,
            marginTop: 8, marginBottom: 24,
          }}
        >
          <Link
            to="/moje-akta-imigracyjne"
            data-testid="wiedza-jak-final-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 700,
              background: '#7DD3FC', color: '#0B1220',
              textDecoration: 'none', fontSize: 15,
            }}
          >
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
            Zacznij od&nbsp;swoich akt
          </Link>
          <Link
            to="/dashboard-akt-imigracyjnych"
            data-testid="wiedza-jak-cta-dashboard"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14,
            }}
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            Hub Akt Imigracyjnych
          </Link>
          <Link
            to="/operator-zgodnosci"
            data-testid="wiedza-jak-cta-compliance"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14,
            }}
          >
            <Users className="w-4 h-4" aria-hidden="true" />
            Operator Zgodności
          </Link>
          <Link
            to="/bezpieczenstwo-i-zgodnosc"
            data-testid="wiedza-jak-cta-security"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14,
            }}
          >
            <ListChecks className="w-4 h-4" aria-hidden="true" />
            Bezpieczeństwo i&nbsp;zgodność
          </Link>
        </div>

        <p
          data-testid="wiedza-jak-doctrine-footer"
          style={{
            marginTop: 36, fontSize: 12, lineHeight: 1.6,
            color: 'rgba(226,232,240,0.55)', fontStyle: 'italic',
          }}
        >
          <Clock className="w-3 h-3 inline" aria-hidden="true" /> AI
          przygotowuje. Władza ludzka decyduje. Każde działanie zostawia ślad
          audytowy.
        </p>
      </div>
    </main>
  );
};

export default WiedzaJakUporzadkowacAktaImigracyjne;
